import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControlLabel,
    Switch,
    Grid,
    CircularProgress,
    Tabs,
    Tab,
    Box,
    IconButton,
    Typography,
    Card,
    CardContent,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Paper,
    Chip
} from "@material-ui/core";
import { Add as AddIcon, Delete as DeleteIcon } from "@material-ui/icons";
import { toast } from "react-toastify";
import ImageUpload from "../ImageUpload";
import * as campaignLandingPagesService from "../../services/campaignLandingPages";
import useWhatsApps from "../../hooks/useWhatsApps";

function TabPanel({ children, value, index }) {
    return (
        <div hidden={value !== index} style={{ paddingTop: 20 }}>
            {value === index && children}
        </div>
    );
}

const CampaignLandingPageModal = ({ open, onClose, landingPageId, onSave }) => {
    const { whatsApps } = useWhatsApps();
    const [loading, setLoading] = useState(false);
    const [currentTab, setCurrentTab] = useState(0);
    const [landingPage, setLandingPage] = useState({
        name: "",
        slug: "",
        title: "",
        subtitle: "",
        description: "",
        buttonText: "Começar Agora",
        isActive: true,
        headerLogo: "",
        headerMenuLinks: [],
        heroImage: "",
        heroBackground: "", // New field
        heroCtaText: "Começar Agora",
        heroCtaLink: "",
        benefits: [],
        steps: [],
        testimonials: [],
        plans: [],
        faq: [],
        footerLinks: [],
        footerSocialMedia: [],
        footerContact: "",
        footerCopyright: "© 2025 Todos os direitos reservados",
        template: "standard",
        primaryColor: "#6366f1",
        secondaryColor: "#8b5cf6",
        fontFamily: "Inter, sans-serif",
        facebookPixelId: "",
        googleTagId: "",
        sourceType: "quiz",
        quizQuestions: [],
        pwaConfig: {
            botName: "Dzapy Atendimento",
            botAvatar: "",
            initialMessage: "Olá, vi seu anúncio no facebook e gostaria de saber mais informações."
        },
        startMessageEnabled: true,
        queueId: null,
        whatsappId: null,
        titleColor: "#000000"
    });

    const safeParse = (data, defaultValue = []) => {
        if (!data) return defaultValue;
        if (typeof data === 'object') return data;
        try {
            const parsed = JSON.parse(data);
            return parsed || defaultValue;
        } catch (e) {
            return defaultValue;
        }
    };

    useEffect(() => {
        if (landingPageId) {
            (async () => {
                try {
                    const data = await campaignLandingPagesService.get(landingPageId);
                    setLandingPage({
                        ...data,
                        benefits: safeParse(data.benefits),
                        steps: safeParse(data.steps),
                        testimonials: safeParse(data.testimonials),
                        plans: safeParse(data.plans),
                        faq: safeParse(data.faq),
                        headerMenuLinks: safeParse(data.headerMenuLinks),
                        footerLinks: safeParse(data.footerLinks),
                        footerSocialMedia: safeParse(data.footerSocialMedia),
                        quizQuestions: safeParse(data.quizQuestions),
                        pwaConfig: safeParse(data.pwaConfig, {
                            botName: "Dzapy Atendimento",
                            botAvatar: "",
                            initialMessage: "Olá, vi seu anúncio no facebook e gostaria de saber mais informações."
                        }),
                        template: data.template || "standard",
                        heroBackground: data.heroBackground || "", // Load new field
                        whatsappId: data.whatsappId || null,
                        titleColor: data.titleColor || "#000000"
                    });
                } catch (err) {
                    toast.error("Erro ao carregar landing page");
                }
            })();
        } else {
            setLandingPage({
                name: "",
                slug: "",
                title: "",
                subtitle: "",
                description: "",
                buttonText: "Começar Agora",
                isActive: true,
                headerLogo: "",
                headerMenuLinks: [],
                heroImage: "",
                heroBackground: "", // New field
                heroCtaText: "Começar Agora",
                heroCtaLink: "",
                benefits: [],
                steps: [],
                testimonials: [],
                plans: [],
                faq: [],
                footerLinks: [],
                footerSocialMedia: [],
                footerContact: "",
                footerCopyright: "© 2025 Todos os direitos reservados",
                template: "standard",
                primaryColor: "#6366f1",
                secondaryColor: "#8b5cf6",
                fontFamily: "Inter, sans-serif",
                facebookPixelId: "",
                googleTagId: "",
                sourceType: "quiz",
                quizQuestions: [],
                pwaConfig: {
                    botName: "Dzapy Atendimento",
                    botAvatar: "",
                    initialMessage: "Olá, vi seu anúncio no facebook e gostaria de saber mais informações."
                },
                startMessageEnabled: true,
                queueId: null,
                whatsappId: null,
                titleColor: "#000000"
            });
        }
    }, [landingPageId, open]);

    const handleClose = () => {
        setCurrentTab(0);
        onClose();
    };

    const handleSave = async () => {
        if (!landingPage.name || !landingPage.slug || !landingPage.title) {
            toast.error("Preencha os campos obrigatórios");
            return;
        }

        setLoading(true);
        try {
            let savedLandingPage;
            if (landingPageId) {
                savedLandingPage = await campaignLandingPagesService.update(landingPageId, landingPage);
                toast.success("Landing page atualizada com sucesso");
            } else {
                savedLandingPage = await campaignLandingPagesService.create(landingPage);
                toast.success("Landing page criada com sucesso");
            }
            onSave(savedLandingPage);
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao salvar landing page");
        }
        setLoading(false);
    };

    const handleChange = (e) => {
        const { name, value, checked, type } = e.target;
        setLandingPage((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value
        }));

        if (name === "name" && !landingPageId) {
            const slug = value
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/(^-|-$)/g, "");
            setLandingPage((prev) => ({ ...prev, slug }));
        }
    };

    const addBenefit = () => {
        setLandingPage(prev => ({
            ...prev,
            benefits: [...prev.benefits, { title: "", description: "", icon: "" }]
        }));
    };

    const removeBenefit = (index) => {
        setLandingPage(prev => ({
            ...prev,
            benefits: prev.benefits.filter((_, i) => i !== index)
        }));
    };

    const updateBenefit = (index, field, value) => {
        setLandingPage(prev => ({
            ...prev,
            benefits: prev.benefits.map((b, i) => i === index ? { ...b, [field]: value } : b)
        }));
    };

    const addStep = () => {
        setLandingPage(prev => ({
            ...prev,
            steps: [...prev.steps, { title: "", description: "" }]
        }));
    };

    const removeStep = (index) => {
        setLandingPage(prev => ({
            ...prev,
            steps: prev.steps.filter((_, i) => i !== index)
        }));
    };

    const updateStep = (index, field, value) => {
        setLandingPage(prev => ({
            ...prev,
            steps: prev.steps.map((s, i) => i === index ? { ...s, [field]: value } : s)
        }));
    };

    const addTestimonial = () => {
        setLandingPage(prev => ({
            ...prev,
            testimonials: [...prev.testimonials, { name: "", role: "", text: "" }]
        }));
    };

    const removeTestimonial = (index) => {
        setLandingPage(prev => ({
            ...prev,
            testimonials: prev.testimonials.filter((_, i) => i !== index)
        }));
    };

    const updateTestimonial = (index, field, value) => {
        setLandingPage(prev => ({
            ...prev,
            testimonials: prev.testimonials.map((t, i) => i === index ? { ...t, [field]: value } : t)
        }));
    };

    const addPlan = () => {
        setLandingPage(prev => ({
            ...prev,
            plans: [...prev.plans, { name: "", price: "", description: "", buttonText: "Escolher Plano", featured: false }]
        }));
    };

    const removePlan = (index) => {
        setLandingPage(prev => ({
            ...prev,
            plans: prev.plans.filter((_, i) => i !== index)
        }));
    };

    const updatePlan = (index, field, value) => {
        setLandingPage(prev => ({
            ...prev,
            plans: prev.plans.map((p, i) => i === index ? { ...p, [field]: value } : p)
        }));
    };

    const addFaq = () => {
        setLandingPage(prev => ({
            ...prev,
            faq: [...prev.faq, { question: "", answer: "" }]
        }));
    };

    const removeFaq = (index) => {
        setLandingPage(prev => ({
            ...prev,
            faq: prev.faq.filter((_, i) => i !== index)
        }));
    };

    const updateFaq = (index, field, value) => {
        setLandingPage(prev => ({
            ...prev,
            faq: prev.faq.map((f, i) => i === index ? { ...f, [field]: value } : f)
        }));
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: {
                    borderRadius: "12px",
                    boxShadow: "0 8px 40px rgba(0,0,0,0.25)",
                    padding: "10px"
                }
            }}
        >
            <DialogTitle>
                {landingPageId ? "Editar Landing Page" : "Nova Landing Page"}
            </DialogTitle>
            <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)} variant="scrollable" scrollButtons="auto">
                <Tab label="Básico" />
                <Tab label="Modelos" />
                <Tab label="Header" />
                <Tab label="Hero" />
                <Tab label="Benefícios" />
                <Tab label="Como Funciona" />
                <Tab label="Depoimentos" />
                <Tab label="Planos" />
                <Tab label="FAQ" />
                <Tab label="Footer" />
                <Tab label="Estilo" />
                <Tab label="Captura de Leads" />
                <Tab label="Resposta Lead" />
            </Tabs>
            <DialogContent dividers style={{ minHeight: 400 }}>
                {/* Tab 0: Básico */}
                <TabPanel value={currentTab} index={0}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Nome *" name="name" value={landingPage.name} onChange={handleChange} variant="outlined" fullWidth margin="dense" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Slug (URL) *" name="slug" value={landingPage.slug} onChange={handleChange} variant="outlined" fullWidth margin="dense" helperText={`URL: ${window.location.origin}/lp/${landingPage.slug || "..."}`} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Título *" name="title" value={landingPage.title} onChange={handleChange} variant="outlined" fullWidth margin="dense" />
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <TextField label="Subtítulo" name="subtitle" value={landingPage.subtitle} onChange={handleChange} variant="outlined" fullWidth margin="dense" />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                            <TextField label="Cor do Título" name="titleColor" value={landingPage.titleColor || "#000000"} onChange={handleChange} variant="outlined" fullWidth margin="dense" type="color" />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Descrição" name="description" value={landingPage.description} onChange={handleChange} variant="outlined" fullWidth multiline rows={3} margin="dense" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Texto do Botão" name="buttonText" value={landingPage.buttonText} onChange={handleChange} variant="outlined" fullWidth margin="dense" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel control={<Switch checked={landingPage.isActive} onChange={handleChange} name="isActive" color="primary" />} label="Landing Page Ativa" />
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Tab 1: Modelos */}
                <TabPanel value={currentTab} index={1}>
                    <Grid container spacing={2} justifyContent="center">
                        <Grid item xs={12} sm={6}>
                            <Paper
                                elevation={landingPage.template === "standard" ? 4 : 1}
                                style={{
                                    padding: 20,
                                    cursor: "pointer",
                                    border: landingPage.template === "standard" ? "2px solid #6366f1" : "1px solid #e0e0e0",
                                    textAlign: "center",
                                    height: "100%"
                                }}
                                onClick={() => setLandingPage({ ...landingPage, template: "standard" })}
                            >
                                <Box height={150} bgcolor="#f3f4f6" mb={2} display="flex" alignItems="center" justifyContent="center" flexDirection="column">
                                    <Box width="100%" height={30} bgcolor="white" borderBottom="1px solid #ddd" mb={1} />
                                    <Box width="80%" height={80} bgcolor="#e5e7eb" borderRadius={4} />
                                </Box>
                                <Typography variant="h6" gutterBottom>Padrão</Typography>
                                <Typography variant="body2" color="textSecondary">Header branco fixo, Hero ao lado da imagem. Layout clássico e limpo.</Typography>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Paper
                                elevation={landingPage.template === "immersive" ? 4 : 1}
                                style={{
                                    padding: 20,
                                    cursor: "pointer",
                                    border: landingPage.template === "immersive" ? "2px solid #6366f1" : "1px solid #e0e0e0",
                                    textAlign: "center",
                                    height: "100%"
                                }}
                                onClick={() => setLandingPage({ ...landingPage, template: "immersive" })}
                            >
                                <Box height={150} bgcolor="#374151" mb={2} position="relative" display="flex" alignItems="center" justifyContent="center">
                                    <Box position="absolute" top={0} left={0} width="100%" height="100%" bgcolor="rgba(0,0,0,0.3)" />
                                    <Box position="absolute" top={10} width="90%" height={10} bgcolor="rgba(255,255,255,0.5)" borderRadius={4} />
                                    <Typography variant="caption" style={{ color: "white", zIndex: 1 }}>Imagem de Fundo Total</Typography>
                                </Box>
                                <Typography variant="h6" gutterBottom>Imersivo</Typography>
                                <Typography variant="body2" color="textSecondary">Header transparente, Menu sobre a imagem de fundo. Visual moderno e impactante.</Typography>
                            </Paper>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Tab 2: Header */}
                <TabPanel value={currentTab} index={2}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <ImageUpload
                                label="Logo do Header"
                                value={landingPage.headerLogo}
                                onChange={(url) => setLandingPage(prev => ({ ...prev, headerLogo: url }))}
                                recommendedSize="200x60px"
                                helperText="Logo que aparecerá no topo da página"
                            />
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Tab 3: Hero */}
                <TabPanel value={currentTab} index={3}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <ImageUpload
                                label="Imagem Hero (Principal)"
                                value={landingPage.heroImage}
                                onChange={(url) => setLandingPage(prev => ({ ...prev, heroImage: url }))}
                                recommendedSize="600x400px ou PNG transparente"
                                helperText="Imagem principal que aparece ao lado do texto"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <ImageUpload
                                label="Imagem de Fundo (Apenas Modelo Imersivo)"
                                value={landingPage.heroBackground}
                                onChange={(url) => setLandingPage(prev => ({ ...prev, heroBackground: url }))}
                                recommendedSize="1920x1080px (Alta resolução)"
                                helperText="Esta imagem será usada como background total no modelo Imersivo."
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Texto do CTA" name="heroCtaText" value={landingPage.heroCtaText} onChange={handleChange} variant="outlined" fullWidth margin="dense" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Link do CTA" name="heroCtaLink" value={landingPage.heroCtaLink} onChange={handleChange} variant="outlined" fullWidth margin="dense" />
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Tab 4: Benefícios */}
                <TabPanel value={currentTab} index={4}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Benefícios</Typography>
                        <Button startIcon={<AddIcon />} onClick={addBenefit} color="primary">Adicionar</Button>
                    </Box>
                    {landingPage.benefits.map((benefit, index) => (
                        <Card key={index} style={{ marginBottom: 15 }}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle2">Benefício {index + 1}</Typography>
                                    <IconButton size="small" onClick={() => removeBenefit(index)}><DeleteIcon /></IconButton>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField label="Título" value={benefit.title} onChange={(e) => updateBenefit(index, 'title', e.target.value)} variant="outlined" fullWidth size="small" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField label="Descrição" value={benefit.description} onChange={(e) => updateBenefit(index, 'description', e.target.value)} variant="outlined" fullWidth multiline rows={2} size="small" />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    ))}
                </TabPanel>

                {/* Tab 5: Como Funciona */}
                <TabPanel value={currentTab} index={5}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Passos</Typography>
                        <Button startIcon={<AddIcon />} onClick={addStep} color="primary">Adicionar</Button>
                    </Box>
                    {landingPage.steps.map((step, index) => (
                        <Card key={index} style={{ marginBottom: 15 }}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle2">Passo {index + 1}</Typography>
                                    <IconButton size="small" onClick={() => removeStep(index)}><DeleteIcon /></IconButton>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField label="Título" value={step.title} onChange={(e) => updateStep(index, 'title', e.target.value)} variant="outlined" fullWidth size="small" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField label="Descrição" value={step.description} onChange={(e) => updateStep(index, 'description', e.target.value)} variant="outlined" fullWidth multiline rows={2} size="small" />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    ))}
                </TabPanel>

                {/* Tab 6: Depoimentos */}
                <TabPanel value={currentTab} index={6}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Depoimentos</Typography>
                        <Button startIcon={<AddIcon />} onClick={addTestimonial} color="primary">Adicionar</Button>
                    </Box>
                    {landingPage.testimonials.map((testimonial, index) => (
                        <Card key={index} style={{ marginBottom: 15 }}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle2">Depoimento {index + 1}</Typography>
                                    <IconButton size="small" onClick={() => removeTestimonial(index)}><DeleteIcon /></IconButton>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Nome" value={testimonial.name} onChange={(e) => updateTestimonial(index, 'name', e.target.value)} variant="outlined" fullWidth size="small" />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Cargo/Empresa" value={testimonial.role} onChange={(e) => updateTestimonial(index, 'role', e.target.value)} variant="outlined" fullWidth size="small" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField label="Depoimento" value={testimonial.text} onChange={(e) => updateTestimonial(index, 'text', e.target.value)} variant="outlined" fullWidth multiline rows={3} size="small" />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    ))}
                </TabPanel>

                {/* Tab 7: Planos */}
                <TabPanel value={currentTab} index={7}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Planos</Typography>
                        <Button startIcon={<AddIcon />} onClick={addPlan} color="primary">Adicionar</Button>
                    </Box>
                    {landingPage.plans.map((plan, index) => (
                        <Card key={index} style={{ marginBottom: 15 }}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle2">Plano {index + 1}</Typography>
                                    <IconButton size="small" onClick={() => removePlan(index)}><DeleteIcon /></IconButton>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Nome" value={plan.name} onChange={(e) => updatePlan(index, 'name', e.target.value)} variant="outlined" fullWidth size="small" />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Preço" value={plan.price} onChange={(e) => updatePlan(index, 'price', e.target.value)} variant="outlined" fullWidth size="small" placeholder="R$ 99" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField label="Descrição" value={plan.description} onChange={(e) => updatePlan(index, 'description', e.target.value)} variant="outlined" fullWidth size="small" placeholder="por mês" />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField label="Texto do Botão" value={plan.buttonText} onChange={(e) => updatePlan(index, 'buttonText', e.target.value)} variant="outlined" fullWidth size="small" />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel control={<Switch checked={plan.featured} onChange={(e) => updatePlan(index, 'featured', e.target.checked)} color="primary" />} label="Plano em Destaque" />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    ))}
                </TabPanel>

                {/* Tab 8: FAQ */}
                <TabPanel value={currentTab} index={8}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                        <Typography variant="h6">Perguntas Frequentes</Typography>
                        <Button startIcon={<AddIcon />} onClick={addFaq} color="primary">Adicionar</Button>
                    </Box>
                    {landingPage.faq.map((item, index) => (
                        <Card key={index} style={{ marginBottom: 15 }}>
                            <CardContent>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle2">FAQ {index + 1}</Typography>
                                    <IconButton size="small" onClick={() => removeFaq(index)}><DeleteIcon /></IconButton>
                                </Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField label="Pergunta" value={item.question} onChange={(e) => updateFaq(index, 'question', e.target.value)} variant="outlined" fullWidth size="small" />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField label="Resposta" value={item.answer} onChange={(e) => updateFaq(index, 'answer', e.target.value)} variant="outlined" fullWidth multiline rows={3} size="small" />
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    ))}
                </TabPanel>

                {/* Tab 9: Footer */}
                <TabPanel value={currentTab} index={9}>
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <TextField label="Contato" name="footerContact" value={landingPage.footerContact} onChange={handleChange} variant="outlined" fullWidth multiline rows={2} margin="dense" />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Copyright" name="footerCopyright" value={landingPage.footerCopyright} onChange={handleChange} variant="outlined" fullWidth margin="dense" />
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* Tab 10: Estilo */}
                <TabPanel value={currentTab} index={10}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Cor Primária" name="primaryColor" value={landingPage.primaryColor} onChange={handleChange} variant="outlined" fullWidth margin="dense" type="color" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Cor Secundária" name="secondaryColor" value={landingPage.secondaryColor} onChange={handleChange} variant="outlined" fullWidth margin="dense" type="color" />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField label="Fonte" name="fontFamily" value={landingPage.fontFamily} onChange={handleChange} variant="outlined" fullWidth margin="dense" helperText="Ex: Inter, Roboto, Arial" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Facebook Pixel ID" name="facebookPixelId" value={landingPage.facebookPixelId} onChange={handleChange} variant="outlined" fullWidth margin="dense" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Google Tag ID" name="googleTagId" value={landingPage.googleTagId} onChange={handleChange} variant="outlined" fullWidth margin="dense" />
                        </Grid>
                    </Grid>
                </TabPanel>


                {/* Tab 11: Captura de Leads (Quiz ou PWA) */}
                <TabPanel value={currentTab} index={11}>
                    <Typography variant="h6" gutterBottom>Configuração de Captura de Leads</Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                        Escolha como deseja capturar leads: através de um Quiz tradicional ou usando o PWA Conversacional.
                    </Typography>

                    {/* Toggle between Quiz and PWA */}
                    <Box mb={3} p={2} style={{ backgroundColor: "#f5f5f5", borderRadius: 8 }}>
                        <FormControl component="fieldset">
                            <Typography variant="subtitle2" gutterBottom><strong>Tipo de Captura:</strong></Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <Paper
                                        elevation={landingPage.sourceType === "quiz" ? 3 : 0}
                                        style={{
                                            padding: 15,
                                            cursor: "pointer",
                                            border: landingPage.sourceType === "quiz" ? "2px solid #6366f1" : "1px solid #ddd"
                                        }}
                                        onClick={() => setLandingPage({ ...landingPage, sourceType: "quiz" })}
                                    >
                                        <Typography variant="subtitle1"><strong>📝 Quiz Tradicional</strong></Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Formulário com perguntas personalizadas para capturar informações do lead.
                                        </Typography>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Paper
                                        elevation={landingPage.sourceType === "pwa" ? 3 : 0}
                                        style={{
                                            padding: 15,
                                            cursor: "pointer",
                                            border: landingPage.sourceType === "pwa" ? "2px solid #6366f1" : "1px solid #ddd"
                                        }}
                                        onClick={() => setLandingPage({ ...landingPage, sourceType: "pwa" })}
                                    >
                                        <Typography variant="subtitle1"><strong>💬 PWA Conversacional</strong></Typography>
                                        <Typography variant="body2" color="textSecondary">
                                            Experiência de chat do WhatsApp com fluxo conversacional fixo.
                                        </Typography>
                                    </Paper>
                                </Grid>
                            </Grid>
                        </FormControl>
                    </Box>
                    
                    {/* WhatsApp Connection Setting */}
                    <Box mb={3}>
                        <FormControl variant="outlined" fullWidth margin="dense">
                            <InputLabel>Conexão WhatsApp (Opcional)</InputLabel>
                            <Select
                                name="whatsappId"
                                value={landingPage.whatsappId || ""}
                                onChange={handleChange}
                                label="Conexão WhatsApp (Opcional)"
                            >
                                <MenuItem value="">
                                    <em>Padrão do Sistema</em>
                                </MenuItem>
                                {whatsApps.map((wa) => (
                                    <MenuItem key={wa.id} value={wa.id}>
                                        {wa.name} ({wa.number})
                                    </MenuItem>
                                ))}
                            </Select>
                            <Typography variant="caption" color="textSecondary">
                                Selecione qual número de WhatsApp enviará as mensagens automáticas para este lead. Se não selecionado, usará a conexão padrão.
                            </Typography>
                        </FormControl>
                    </Box>

                    {/* Quiz Configuration */}
                    {landingPage.sourceType === "quiz" && (
                        <>
                            <Typography variant="h6" gutterBottom>Perguntas do Quiz</Typography>
                            <Typography variant="body2" color="textSecondary" paragraph>
                                Configure as perguntas que serão exibidas no quiz. O campo de telefone é obrigatório e sempre será a última pergunta.
                            </Typography>

                            {landingPage.quizQuestions && landingPage.quizQuestions.map((question, index) => (
                                <Paper key={index} style={{ padding: 15, marginBottom: 15, backgroundColor: "#f5f5f5" }}>
                                    <Grid container spacing={2} alignItems="center">
                                        <Grid item xs={12} sm={4}>
                                            <FormControl fullWidth variant="outlined" size="small">
                                                <InputLabel>Tipo</InputLabel>
                                                <Select
                                                    value={question.type}
                                                    onChange={(e) => {
                                                        const newQuestions = [...landingPage.quizQuestions];
                                                        newQuestions[index].type = e.target.value;
                                                        setLandingPage({ ...landingPage, quizQuestions: newQuestions });
                                                    }}
                                                    label="Tipo"
                                                >
                                                    <MenuItem value="text">Texto</MenuItem>
                                                    <MenuItem value="options">Múltipla Escolha</MenuItem>
                                                    <MenuItem value="phone">Telefone</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={12} sm={7}>
                                            <TextField
                                                label="Pergunta"
                                                value={question.question}
                                                onChange={(e) => {
                                                    const newQuestions = [...landingPage.quizQuestions];
                                                    newQuestions[index].question = e.target.value;
                                                    setLandingPage({ ...landingPage, quizQuestions: newQuestions });
                                                }}
                                                variant="outlined"
                                                fullWidth
                                                size="small"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={1}>
                                            <IconButton
                                                size="small"
                                                color="secondary"
                                                onClick={() => {
                                                    const newQuestions = landingPage.quizQuestions.filter((_, i) => i !== index);
                                                    setLandingPage({ ...landingPage, quizQuestions: newQuestions });
                                                }}
                                            >
                                                <DeleteIcon />
                                            </IconButton>
                                        </Grid>
                                        {question.type === "options" && (
                                            <Grid item xs={12}>
                                                <TextField
                                                    label="Opções (separadas por vírgula)"
                                                    value={question.options?.join(", ") || ""}
                                                    onChange={(e) => {
                                                        const newQuestions = [...landingPage.quizQuestions];
                                                        newQuestions[index].options = e.target.value.split(",").map(opt => opt.trim());
                                                        setLandingPage({ ...landingPage, quizQuestions: newQuestions });
                                                    }}
                                                    variant="outlined"
                                                    fullWidth
                                                    size="small"
                                                    helperText="Ex: Opção 1, Opção 2, Opção 3"
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                </Paper>
                            ))}

                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<AddIcon />}
                                onClick={() => {
                                    const newQuestion = {
                                        id: `q${Date.now()}`,
                                        type: "text",
                                        question: "",
                                        required: true
                                    };
                                    setLandingPage({
                                        ...landingPage,
                                        quizQuestions: [...(landingPage.quizQuestions || []), newQuestion]
                                    });
                                }}
                                fullWidth
                            >
                                Adicionar Pergunta
                            </Button>
                        </>
                    )}

                    {/* PWA Configuration */}
                    {landingPage.sourceType === "pwa" && (
                        <>
                            <Typography variant="h6" gutterBottom>Configuração do PWA Conversacional</Typography>
                            <Typography variant="body2" color="textSecondary" paragraph>
                                Configure a aparência e mensagem inicial do chat. O fluxo de captura (telefone → nome) é fixo.
                            </Typography>

                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Nome do Bot"
                                        value={landingPage.pwaConfig?.botName || ""}
                                        onChange={(e) => setLandingPage({
                                            ...landingPage,
                                            pwaConfig: { ...landingPage.pwaConfig, botName: e.target.value }
                                        })}
                                        variant="outlined"
                                        fullWidth
                                        margin="dense"
                                        placeholder="Dzapy Atendimento"
                                        helperText="Nome que aparecerá no cabeçalho do chat"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <ImageUpload
                                        label="Avatar do Bot"
                                        value={landingPage.pwaConfig?.botAvatar || ""}
                                        onChange={(url) => setLandingPage({
                                            ...landingPage,
                                            pwaConfig: { ...landingPage.pwaConfig, botAvatar: url }
                                        })}
                                        recommendedSize="200x200px"
                                        helperText="Imagem que aparecerá no cabeçalho do chat (deixe em branco para usar o padrão)"
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField
                                        label="Mensagem Inicial"
                                        value={landingPage.pwaConfig?.initialMessage || ""}
                                        onChange={(e) => setLandingPage({
                                            ...landingPage,
                                            pwaConfig: { ...landingPage.pwaConfig, initialMessage: e.target.value }
                                        })}
                                        variant="outlined"
                                        fullWidth
                                        multiline
                                        rows={3}
                                        margin="dense"
                                        placeholder="Olá, vi seu anúncio no facebook e gostaria de saber mais informações."
                                        helperText="Primeira mensagem que o usuário enviará ao abrir o chat"
                                    />
                                </Grid>
                            </Grid>

                            {/* URL Builder */}
                            <Box mt={3} p={2} style={{ backgroundColor: "#e3f2fd", borderRadius: 8 }}>
                                <Typography variant="subtitle1" gutterBottom><strong>🔗 URL Pública do PWA</strong></Typography>
                                <Typography variant="body2" color="textSecondary" paragraph>
                                    Use esta URL para direcionar seus leads. Você pode adicionar parâmetros UTM para rastreamento.
                                </Typography>

                                <TextField
                                    value={`${window.location.origin}/pwa?id_campanha=${landingPageId || "{ID}"}`}
                                    variant="outlined"
                                    fullWidth
                                    size="small"
                                    InputProps={{
                                        readOnly: true,
                                        endAdornment: (
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    navigator.clipboard.writeText(`${window.location.origin}/pwa?id_campanha=${landingPageId || ""}`);
                                                    toast.success("URL copiada!");
                                                }}
                                            >
                                                📋
                                            </IconButton>
                                        )
                                    }}
                                    style={{ marginBottom: 15 }}
                                />

                                <Typography variant="body2" gutterBottom><strong>Parâmetros opcionais:</strong></Typography>
                                <Typography variant="caption" display="block" color="textSecondary">
                                    • utm_source, utm_medium, utm_campaign, utm_content, utm_term<br />
                                    • mensagem_inicial (sobrescreve a mensagem configurada)<br />
                                    • track_source, track_id (rastreamento personalizado)
                                </Typography>

                                <Box mt={2}>
                                    <Typography variant="caption" display="block" color="textSecondary">
                                        <strong>Exemplo:</strong><br />
                                        {`${window.location.origin}/pwa?id_campanha=${landingPageId || "{ID}"}&utm_source=facebook&utm_medium=cpc&utm_campaign=lancamento`}
                                    </Typography>
                                </Box>
                            </Box>
                        </>
                    )}
                </TabPanel>


                {/* Tab 12: Resposta Lead */}
                <TabPanel value={currentTab} index={12}>
                    <Typography variant="h6" gutterBottom>Mensagem Automática para Leads</Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                        Configure a mensagem que será enviada automaticamente via WhatsApp quando um lead preencher o quiz.
                    </Typography>

                    <Box mb={2}>
                        <Typography variant="body2" gutterBottom>
                            <strong>Variáveis disponíveis:</strong>
                        </Typography>
                        <Box display="flex" gap={1} mb={2}>
                            <Chip label="{nome}" size="small" color="primary" variant="outlined" />
                            <Chip label="{telefone}" size="small" color="primary" variant="outlined" />
                            <Chip label="{email}" size="small" color="primary" variant="outlined" />
                        </Box>
                    </Box>

                    <TextField
                        label="Mensagem de Resposta"
                        name="leadResponseMessage"
                        value={landingPage.leadResponseMessage || ""}
                        onChange={handleChange}
                        variant="outlined"
                        fullWidth
                        multiline
                        rows={8}
                        placeholder="Olá {nome}! 👋&#10;&#10;Obrigado por seu interesse!&#10;&#10;Recebemos suas informações e em breve nossa equipe entrará em contato pelo número {telefone}.&#10;&#10;Enquanto isso, fique à vontade para tirar dúvidas por aqui!"
                        helperText="Use as variáveis acima para personalizar a mensagem. Deixe em branco para não enviar mensagem automática."
                    />

                    {landingPage.leadResponseMessage && (
                        <Box mt={3} p={2} style={{ backgroundColor: "#f5f5f5", borderRadius: 8 }}>
                            <Typography variant="body2" gutterBottom><strong>Preview:</strong></Typography>
                            <Typography variant="body2" style={{ whiteSpace: "pre-wrap" }}>
                                {landingPage.leadResponseMessage
                                    .replace(/{nome}/g, "João Silva")
                                    .replace(/{telefone}/g, "5531985294340")
                                    .replace(/{email}/g, "joao@example.com")}
                            </Typography>
                        </Box>
                    )}
                </TabPanel>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color="secondary" disabled={loading}>Cancelar</Button>
                <Button onClick={handleSave} color="primary" variant="contained" disabled={loading}>
                    {loading ? <CircularProgress size={24} /> : "Salvar"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CampaignLandingPageModal;
